import * as api from "../lib/api.js";
import * as ecapa from "../lib/ecapa.js";
import * as sign from "../lib/sign.js";
import * as store from "../lib/store.js";
import * as telemetry from "../lib/telemetry.js";

import { captureAnalytics } from "./analytics";
import { t } from "./contract/strings";
import { loadNormalisedCredentials } from "./engine/credentials";
import type { PublicConfigPayload } from "./contract/api";
import { flushPushLog, syncSubscription } from "./engine/push";
import {
  announceRotatedToken,
  listenForRotatedTokens,
  rotateTokenIfDue,
  tokenRotationDue,
} from "./engine/token-rotation";

/** The most a due token rotation may delay boot (#436); see the call site. */
const ROTATION_WAIT_MS = 8_000;
import {
  attemptConfig,
  retryConfig,
  type ConfigAvailability,
} from "./engine/server-config";
import { checkDeviceAfterSignedOutBoot, watchForEndedSignIn } from "./engine/session-ended";
import { consumeSignInFragment, refreshAccount } from "./engine/signin";
import { startHomeData } from "./engine/home-data";
import { releaseBootOverlay, stopBootMark } from "./boot-overlay";
import { welcomeDue } from "./routes/guard-policy";
import { installUpdateFlow } from "./engine/update";
import { within } from "./engine/within";
import { disclosureFor, useApp } from "./state/store";
import { buttonClass } from "./ui/button";

/**
 * What has to happen before the app is usable, in the order it has to happen in.
 *
 * The order is the specification, not the steps. Each constraint below was paid for:
 *
 *  1. **Reset guard first.** The inline hatch in `index.html` may already be deleting
 *     caches and about to reload; booting would race it.
 *  2. **Secure context, then Ed25519.** Both are "this browser cannot run this app at
 *     all", and finding either out after an 84 MB download wastes somebody's data to tell
 *     them something knowable in a millisecond.
 *  3. **`/v1/config` first**, because the disclosure it composes is read *before* anybody
 *     agrees to be recorded. Exactly *one* attempt is awaited here; when it fails the retry
 *     runs detached and applies its answer to the store whenever it lands.
 *  4. **Stored credentials**, so the first screen knows who this device is rather than
 *     saying so a moment later.
 *  5. **The sign-in fragment, and the account refresh.** See the comments at those steps.
 *  6. **The service worker last**, after everything above. Nothing here waits on it.
 *
 * **The voice model is not here any more** (#659). Boot used to end with the 84 MB download
 * and its progress bar, so signing in, reading past meetings or opening notes all waited on
 * something none of them needs -- and [V] when it wedged (#643), the whole app did. Home
 * warms it in the background and the screens that need it wait for it themselves; see
 * `engine/model-readiness.ts`.
 *
 * It owns the static `#boot` overlay directly rather than rendering it. The overlay is in
 * `index.html` because it has to be on screen before any JavaScript runs, so the code that
 * takes it down is not React's either.
 */

const el = (id: string) => document.getElementById(id);

/**
 * Bounds on every wait in boot (#643), so the boot screen can never sit on "Getting ready"
 * forever. [V] Two phones did, on 2026-10-05, and the cause was never pinned down: nothing
 * before the model download had a time limit, so any one of these stalling looked exactly
 * the same.
 *
 * - `/v1/config` is aborted after `CONFIG_TIMEOUT_MS`; boot already carries on without an
 *   answer and retries in the background.
 * - The on-device reads (the Ed25519 probe, IndexedDB) get `LOCAL_WAIT_MS`. They answer in
 *   milliseconds; past this something is wedged.
 * - The sign-in link is not aborted -- the token is single-use, and dropping an answer the
 *   server already committed would spend it -- so boot stops *waiting* after
 *   `SIGN_IN_WAIT_MS` and the outcome lands whenever it arrives.
 *
 * The token rotation has its own bound (`ROTATION_WAIT_MS`), the account refresh its own
 * timeout, and the service worker is registered after all of it.
 */
const CONFIG_TIMEOUT_MS = 8_000;
const LOCAL_WAIT_MS = 8_000;
const SIGN_IN_WAIT_MS = 20_000;
const TIMED_OUT = Symbol("timed out");

/**
 * A handle for `tools/ort_probe.mjs`, and only when it asks.
 *
 * The probe is the deploy gate: it loads the real page, runs one embedding through the real
 * runtime and checks the vector is 192-dim and unit-norm. It used to reach the model through
 * `import("/lib/ecapa.js")`, which exists on a dev server and does not exist in the build --
 * ecapa is inside the hashed bundle -- so the gate could not run against what actually
 * ships.
 *
 * Gated on `?probe=1` rather than exposed always. The security cost of an unconditional
 * handle is close to nil here (COEP `require-corp` and no cross-origin subresources mean
 * there is no untrusted script on the page, and anything that could read this could already
 * do everything the page can) -- but it is still a debug surface, this project does not ship
 * those, and the probe owns the URL it navigates to. Distinct from `?reset=`.
 */
function exposeProbeHook(): void {
  if (new URLSearchParams(location.search).get("probe") !== "1") return;
  (globalThis as unknown as { __meetfolks?: unknown }).__meetfolks = { ecapa };
}

/**
 * Put a `/v1/config` answer -- or the definite lack of one -- into the store.
 *
 * One function for both so the body, the sentence composed from it and the availability
 * state can never be written apart from each other.
 */
function applyConfig(config: PublicConfigPayload | null, state: ConfigAvailability): void {
  useApp.getState().setServerConfig(config, disclosureFor(config), state);
}

/** The boot overlay is the only surface there is until it comes down. */
function bootText(message: string) {
  const text = el("boot-text");
  if (text) text.textContent = message;
}

/** Boot has stopped short: say why, under a mark that holds still (#1066). */
function bootStopped(message: string) {
  bootText(message);
  stopBootMark(el("boot"));
}

/**
 * A way out of a failed boot that does not depend on knowing `?reset=app` (#643).
 *
 * The one boot failure left is the device's stored identity not reading; the tap reloads.
 * The shell, the saved model and the voice profile all stay -- nothing is wrong with them.
 */
function showBootRetry() {
  const overlay = el("boot");
  if (!overlay || overlay.querySelector("button")) return;
  bootStopped(t("boot.load_failed_retry"));
  const retry = document.createElement("button");
  retry.type = "button";
  retry.className = buttonClass("ghost");
  retry.textContent = t("common.try_again");
  retry.addEventListener("click", () => {
    captureAnalytics("retry_tapped", { surface: "boot" });
    retry.disabled = true;
    location.reload();
  });
  overlay.append(retry);
}

/**
 * An existing user never sees the first-run Welcome (#1095).
 *
 * The Welcome is owed to a device with nothing on it, and signing out, or a sign-in ended
 * elsewhere (#854), wipes a device back to exactly that. So the moment a device is seen
 * with an account, credentials, a voice or a key -- at boot, or on signing in later -- it
 * is marked past the Welcome for good, before any wipe can make it look new.
 */
function retireWelcomeForExistingDevices(): void {
  const check = (state: ReturnType<typeof useApp.getState>) => {
    if (state.welcomeDone) return;
    if (state.account || state.credentials || state.template || state.keypair) state.finishWelcome();
  };
  check(useApp.getState());
  useApp.subscribe(check);
}

/**
 * The static footer's three destinations (#1085): a fixed enum, never the URL.
 *
 * The footer is in `index.html`, outside React, so it is wired here once at boot. Only
 * the three known paths are counted; any other link reports nothing.
 */
function installFooterLinkAnalytics(): void {
  const links: Record<string, "privacy" | "terms" | "support"> = {
    "/privacy/": "privacy",
    "/terms/": "terms",
    "/support/": "support",
  };
  for (const anchor of document.querySelectorAll<HTMLAnchorElement>(".site-foot a")) {
    anchor.addEventListener("click", () => {
      let path = "";
      try {
        path = new URL(anchor.href).pathname;
      } catch {
        return;
      }
      const link = links[path];
      if (link) captureAnalytics("link_opened", { link });
    });
  }
}

export async function boot() {
  // The inline handler in index.html is mid-reset and about to reload. Booting would race
  // it -- loading 84 MB into a cache that is being deleted, and asking for the microphone
  // on a page that is going away.
  if (window.__meetfolksReset) return;

  installFooterLinkAnalytics();
  retireWelcomeForExistingDevices();
  exposeProbeHook();

  if (!window.isSecureContext) {
    bootStopped(
      t("boot.https")
    );
    return;
  }

  // Every claim this device makes is signed, so a browser that cannot mint the key cannot
  // take part -- and saying so here is the difference between a clear sentence and a
  // failure at the moment somebody presses record. A probe that never answers is not a "no":
  // boot carries on, and signing fails loudly later if it really is broken.
  if (!(await within(sign.isSupported(), LOCAL_WAIT_MS, true))) {
    bootStopped(
      t("boot.unsupported")
    );
    return;
  }

  // A deployment that cannot be asked is not a deployment that cannot be used: joining and
  // recording run on the device token. The disclosure falls back to the one claim that holds
  // regardless, and the rest of boot continues.
  //
  // But it is not free either, and it used to be recorded as an absence and forgotten.
  // [V] When `accepts_audio` gated the per-chunk upload and the whole-session backstop
  // (both retired with the batch lane, #242), a single lost request here cost this device
  // every word of its own transcript and left nothing in the logs. #124. Today only an
  // explicit `accepts_audio: false` stops recording (`recordingPaused`, #388), so a lost
  // answer no longer blocks anything, but the disclosure still wants it. So: one awaited
  // attempt, exactly as before, then three more in the background. Each one is bounded: an
  // unanswered request here held the whole boot, model and all.
  const fetchConfig = () => api.config({ timeoutMs: CONFIG_TIMEOUT_MS });
  const first = await attemptConfig(fetchConfig);
  let serverConfig = first.config;
  applyConfig(first.config, first.config ? "ok" : "pending");

  // Detached on purpose, so it costs the boot nothing. Awaiting it would put up to ten
  // seconds of retry in front of a screen that is perfectly usable without an answer.
  if (!serverConfig) {
    void retryConfig({
      fetchConfig,
      // Late is not too late for the two that matter: the disclosure and the audio gate
      // both re-read this from the store when a screen renders. The one reader a late
      // answer can miss is `syncSubscription` below, which takes the value once and by
      // then has usually already run -- a stale push subscription costs a notification
      // and is reconciled on the next boot.
      onConfig: (config) => {
        serverConfig = config;
        applyConfig(config, "ok");
      },
      // The only definite negative there is. Everything downstream reads it from the store
      // rather than inferring it from a null -- see `server-config.ts`.
      onExhausted: () => applyConfig(null, "unavailable"),
    });
  }

  const identity = await within(
    Promise.all([
      // Normalised, not read raw: a device that enrolled on the previous client stored
      // `claimProfileId` and this one reads `claim_profile_id`. See `credentials.ts` for what that costs
      // a device that has already been set up -- it is every claim it will ever make.
      loadNormalisedCredentials(),
      store.loadTemplate(),
      store.loadKeypair(),
    ]),
    LOCAL_WAIT_MS,
    TIMED_OUT
  );
  // Never carried on without: an empty identity would read as a device that was never set
  // up, and setting it up again would replace a signing key that still exists. A reload is
  // the way out; the model stays cached because nothing is wrong with it.
  if (identity === TIMED_OUT) {
    console.warn("This device's stored identity did not load");
    showBootRetry();
    return;
  }
  const [credentials, template, keypair] = identity;
  useApp.getState().setIdentity({ credentials, template, keypair });
  if (credentials) api.setToken(credentials.token);
  // A sign-in ended elsewhere is noticed on any refused call and on return to the page (#854).
  watchForEndedSignIn();

  // Reconcile the server's push subscription with what this browser actually
  // holds. Never prompts -- it returns immediately unless the permission is
  // already granted -- and never blocks boot, because a stale subscription costs
  // a notification and starting the app costs a session.
  //
  // Needed because the two can drift with nothing to notice: a push service
  // rotates an endpoint, `sw.js` re-subscribes in `pushsubscriptionchange`, and
  // that handler has no device token with which to register the new one. This is
  // the next moment anything does.
  //
  // Report whatever the service worker handled while the app was closed. This is
  // the only moment those events can be sent -- the worker has no device token --
  // and it has to come after `api.setToken` above, or the flush that carries them
  // is unauthenticated and dropped.
  //
  // Both wait for the token rotation (#436), which is usually a no-op: once it is
  // due, the old token dies the moment the server commits, and a request still
  // carrying it would be refused.
  //
  // Boot itself waits too, but only when a rotation is due and only for a bounded
  // time: the sign-in fragment below links this device with its token, and on
  // the first boot after this shipped every device rotates at once. The cap keeps
  // a hung network from holding up the screen; the rotation is never aborted
  // (that could lose the only copy of the new token), it just finishes behind.
  if (credentials) {
    const adopt = (current: typeof credentials) => {
      api.setToken(current.token);
      useApp.setState({ credentials: current });
    };
    listenForRotatedTokens(() => useApp.getState().credentials, adopt);
    const due = tokenRotationDue(credentials, Date.now());
    const rotation = rotateTokenIfDue(credentials).then((current) => {
      if (current !== credentials) {
        adopt(current);
        announceRotatedToken(current);
      }
      void syncSubscription(serverConfig);
      void flushPushLog();
    });
    if (due) {
      await Promise.race([rotation, new Promise((resolve) => setTimeout(resolve, ROTATION_WAIT_MS))]);
    }
  }

  // A magic link is good for ten minutes and the account screen needs no scoring at all.
  // [V] When this used to wait behind the model download, a slow connection burnt most of
  // the link's life on bytes it did not need, and a failed download meant the fragment was
  // never read -- somebody tapped a valid link, sat through a model error, and was never
  // signed in. Boot no longer loads the model at all (#659).
  //
  // Waited for up to `SIGN_IN_WAIT_MS`, never aborted (see the bound's comment). One that
  // answers later is shown when it does, the way a link tapped mid-session is.
  const signingIn = consumeSignInFragment();
  const answered = await within(signingIn, SIGN_IN_WAIT_MS, TIMED_OUT);
  if (answered === TIMED_OUT) {
    void signingIn
      .then((late) => {
        if (late) useApp.getState().setSignInOutcome(late);
      })
      .catch(() => {});
  }
  const signIn = answered === TIMED_OUT ? null : answered;

  // Not left until after the home screen renders: the Create button says one of two
  // different things depending on the answer, and a signed-in device would otherwise be told
  // to sign in for a moment on every single boot. Bounded by its own timeout.
  //
  // Once `/me` confirms the stored sign-in, Home's wallet and recent meetings start beside
  // the device link (#1066). Never awaited: they are content, not a routing decision, so the
  // waits above, the failure rule and Try again are unchanged, and Home uses them when it
  // mounts instead of asking again (`engine/home-data.ts`).
  await (signIn
    ? null
    : refreshAccount((account) => startHomeData(account.account_id)).catch(() => null));
  // No account, but a device: if its token is refused too, the sign-in it had was ended
  // elsewhere -- the account deleted, most likely -- and the device is wiped and told (#854).
  if (!signIn) await checkDeviceAfterSignedOutBoot().catch(() => {});
  // Unconditionally, because "we did not ask" is also an answer as far as the guards are
  // concerned: a fragment that reported an error skips the refresh above, and a route that
  // waited for one that is never coming would render nothing at all.
  useApp.getState().setAccountLoaded(true);
  // Signed in, the overlay stays up until the app has drawn the screen under it
  // (`BootRelease` in `app.tsx`, #1043), so no frame in between shows the shell without
  // one. Signed out, it comes down here as it always has.
  if (!useApp.getState().account) {
    // Onto the first-run intro (#1095), whose mark is this one at the same size and place:
    // the overlay fades without shrinking its mark, so there is never a second mark under it.
    if (welcomeDue(useApp.getState())) el("boot")?.classList.add("to-welcome");
    releaseBootOverlay(el("boot"));
  }
  if (signIn) useApp.getState().setSignInOutcome(signIn);
  useApp.getState().setReady(true);

  // `crossOriginIsolated` is the one boot fact that decides everything downstream
  // (ECAPA runs on one thread without it) and is otherwise silent -- see `ecapa.js`.
  //
  // The thread and core counts travel beside it because `ecapa.js` names their absence as
  // the thing blocking a live question: one handset "gained only 1.17x and is now the sole
  // bottleneck at roughly 88% duty cycle. [T] Why that phone gains so little is open, and
  // the thread count it actually got is not recoverable from the server logs --
  // `updateCapabilities` does not send it." Isolation alone does not answer it: a device can
  // be isolated and still be handed two threads because that is all it has.
  //
  // The threads ORT actually took, and whether inference ended up off the main thread, are
  // only known once the model is loaded, which boot no longer waits for: `model_load`
  // carries those (`engine/model-readiness.ts`).
  telemetry.log("boot", {
    cross_origin_isolated: window.crossOriginIsolated,
    cores: navigator.hardwareConcurrency ?? null,
  });

  // Last, deliberately -- see the order above.
  installUpdateFlow();
}

/**
 * A sign-in link tapped while the app is already open.
 *
 * Changing only the fragment of the URL a tab is already showing does not reload the page,
 * so `boot()` never runs again and the link would silently do nothing -- and this is the
 * *likely* case, not an exotic one: the app is installed, the person opens their mail,
 * taps, and lands back on the tab they already had. [V] Caught in a browser probe, where
 * the second navigation was a no-op and no sign-in happened.
 */
export function watchForSignInLinks(): void {
  window.addEventListener("hashchange", async () => {
    const outcome = await consumeSignInFragment();
    if (outcome) useApp.getState().setSignInOutcome(outcome);
  });
}
